import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiError } from "../api/client";
import { setAccessToken } from "../api/token";
import type { SellerProfile } from "../api/types";
import { fetchMe, login as loginRequest, logout as logoutRequest, refresh } from "../api/auth";

type Status = "hydrating" | "authenticated" | "anonymous";

interface AuthValue {
  status: Status;
  seller: SellerProfile | null;
  signIn: (identifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-read the profile after a mutation changed it. */
  reloadSeller: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Auth state for the panel.
 *
 * The hydration gate is the point of this file. On boot the panel calls
 * `POST /auth/refresh` with the httpOnly cookie; only once that settles does it
 * decide whether the user is signed in. Without it, a reload of an authenticated
 * session flashes the login form for a frame and then bounces — which reads as
 * "it logged me out". The `hydrating` status exists only to hold that decision,
 * and the router renders a loader for it rather than a redirect.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("hydrating");
  const [seller, setSeller] = useState<SellerProfile | null>(null);

  const loadSeller = useCallback(async () => {
    try {
      setSeller(await fetchMe());
    } catch (err) {
      // A profile is optional: a seller who has not created one yet still gets a
      // panel, and `POST /sellers` is offered instead of a broken dashboard.
      if (err instanceof ApiError && err.isUnauthorised) setSeller(null);
      else setSeller(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await refresh();
        if (cancelled) return;
        await loadSeller();
        if (!cancelled) setStatus("authenticated");
      } catch {
        // No valid refresh cookie. This is the ordinary signed-out path, not an
        // error worth surfacing.
        if (!cancelled) setStatus("anonymous");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadSeller]);

  const signIn = useCallback(
    async (identifier: string, password: string) => {
      const result = await loginRequest(identifier, password);
      setAccessToken(result.accessToken);
      await loadSeller();
      setStatus("authenticated");
    },
    [loadSeller],
  );

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      // Clear locally even if the call failed: leaving a token in memory after
      // the user asked to sign out is worse than a stale cookie.
      setAccessToken(null);
      setSeller(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ status, seller, signIn, signOut, reloadSeller: loadSeller }),
    [status, seller, signIn, signOut, loadSeller],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Co-located with the provider on purpose: a hook that consumed a context from a
 * separate module would still need that module's import, and splitting the pair
 * adds a file for no isolation gain. The rule exists to stop a module being both
 * a fast-refresh boundary and a re-export barrel, which this is not.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}